import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {usePageDataContext} from '@github-ui/pull-request-page-data-tooling/page-data-context'
import type {StatusChecksPageData} from '../payloads/status-checks'
import {fetchWithTracing} from '../../helpers/fetch-with-tracing'
import {useQuery, useQueryClient, useSuspenseQuery} from '@github-ui/react-query'

// Non-infinity stale so the checks data mimics stale-while-revalidate behavior
// The default Infinity value prevents the checks data from being updated on mount
// This needs to be non-0 so that we don't duplicate the requests between
// MergeBox and ChecksSection during the initial load
const STALE_TIME = 10_000 // 10 seconds

export function useStatusChecksPageDataQueryKey({pullRequestHeadSha}: {pullRequestHeadSha: string}) {
  const {basePageDataUrl} = usePageDataContext()
  return [PageData.statusChecks, `headSha:${pullRequestHeadSha}`, `basePageDataURL:${basePageDataUrl}`]
}

/**
 * Suspense + throw-on-error variant. Use this from components that own status
 * checks as their primary concern and sit behind their own `Suspense` /
 * `ErrorBoundary` (e.g. `ChecksSection`, `useStatusChecksLiveUpdates`).
 *
 * Do NOT use this from components that only *read* checks data as an input to a
 * larger calculation (e.g. the `MergeBox` rollup status). Prefer
 * `useStatusChecksPageDataWithoutError` there — see that hook's doc comment.
 */
export function useStatusChecksPageData({pullRequestHeadSha}: {pullRequestHeadSha: string}) {
  const apiURL = usePageDataUrl(PageData.statusChecks)
  const queryKey = useStatusChecksPageDataQueryKey({pullRequestHeadSha})
  const queryClient = useQueryClient()

  return useSuspenseQuery({
    queryKey,
    queryFn: async () => {
      return fetchWithTracing<StatusChecksPageData>(apiURL, queryKey, queryClient)
    },
    staleTime: STALE_TIME,
  })
}

/**
 * Non-suspense, non-throwing variant. Use this from components that need to
 * read checks data to derive other state (e.g. `MergeBox`'s rollup status, which
 * depends on the `mergeable_if_statuses_pass` MergeRequirements state) but
 * should NOT suspend or fail on the checks endpoint.
 *
 * Two reasons this variant exists:
 *
 * 1. Error isolation. `MergeRequirements` and status checks are decoupled and
 *    refetch independently for live updates. If the parent used the suspense
 *    variant, a `/status-checks` failure would throw past the inner `Suspense`
 *    and trip the MergeBox `ErrorBoundary`, replacing reviewers, conflicts,
 *    the merge button, etc. with an error state — even though only the checks
 *    section is actually broken. `ChecksSection` has its own boundary
 *    (`ChecksSectionFetchFailure`) that should absorb those errors.
 * 2. No re-suspending on live updates. Live-update refetches that miss the
 *    cache would otherwise re-suspend the entire MergeBox and flash the
 *    loading spinner over the whole UI on every status check tick. `useQuery`
 *    keeps the previous `data` while it revalidates in the background.
 */
export function useStatusChecksPageDataWithoutError({pullRequestHeadSha}: {pullRequestHeadSha: string}) {
  const apiURL = usePageDataUrl(PageData.statusChecks)
  const queryKey = useStatusChecksPageDataQueryKey({pullRequestHeadSha})
  const throwOnError = false
  const queryClient = useQueryClient()

  return useQuery({
    queryKey,
    throwOnError,
    queryFn: async () => {
      return fetchWithTracing<StatusChecksPageData>(apiURL, queryKey, queryClient)
    },
    staleTime: STALE_TIME,
  })
}
